import { useState } from 'react';
import {
  openedSchedule,
  splitPhoneCategories,
  programProgressPercent,
  type Channel,
  type GuideCategory,
  type Program,
  type ScheduleRow,
} from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

/**
 * La Guida sul telefono: **un elenco di canali che si aprono**, non una griglia.
 *
 * ## Perché non è la griglia rimpicciolita
 *
 * La griglia del televisore ha due assi: i canali in verticale, il tempo in orizzontale. Su 390 px
 * il secondo asse non ci sta — a quattro ore di palinsesto resterebbero poche decine di pixel per
 * programma — e scorrerlo in orizzontale col pollice mentre si scorre in verticale è un gesto che
 * non riesce.
 *
 * Qui il tempo **si apre**: si tocca un canale e il palinsesto scende sotto la sua riga
 * (`docs/04-SCHERMATE-MOBILE.md` §4). È la traduzione onesta di una griglia su uno schermo alto e
 * stretto, non una semplificazione.
 *
 * ## La stella agisce subito, e non apre niente
 *
 * È accanto a ogni canale perché sul telefono **non esiste una schermata dei preferiti**: questo è
 * l'unico posto da cui si aggiungono e si tolgono. Toccandola non si apre la riga — sono due
 * bersagli distinti sulla stessa riga, e confonderli vorrebbe dire aprire un palinsesto ogni volta
 * che si vuole mettere una stella.
 *
 * ## Un canale senza palinsesto si apre lo stesso
 *
 * L'EPG non copre tutto. Aprendo un canale senza programmi si vede che non ce ne sono, e `Guarda
 * qui` resta: è l'azione utile anche quando non si sa cosa stia andando in onda. Nascondere la riga
 * o impedirne l'apertura toglierebbe un canale guardabile per un dato mancante.
 */
export function PhoneGuideScreen({
  channels,
  programs,
  favorites,
  reminders,
  nowMs,
  openChannelId,
  onToggleOpen,
  onToggleFavorite,
  onToggleReminder,
  onWatch,
  categories,
  activeCategoryId,
  onPickCategory,
}: {
  readonly channels: ReadonlyArray<Channel>;
  readonly programs: ReadonlyArray<Program>;
  readonly favorites: ReadonlyArray<string>;
  readonly reminders: ReadonlyArray<string>;
  readonly nowMs: number;
  /** Il canale aperto, o `null`. **Uno solo**: due aperti insieme spingono tutto il resto fuori. */
  readonly openChannelId: string | null;
  readonly onToggleOpen: (channelId: string) => void;
  readonly onToggleFavorite: (channelId: string) => void;
  /**
   * Metti o togli il promemoria.
   *
   * Canale e istante, **non** l'identificativo del programma: quello vale `canale-slot`, dove lo
   * slot è una posizione nel magazzino che cambia quando cambia il palinsesto. L'identità stabile —
   * quella che usano l'archivio e la schermata del televisore — è `reminderKey(canale, istante)`.
   */
  readonly onToggleReminder: (channelId: string, startMs: number) => void;
  readonly onWatch: (channelId: string) => void;
  /**
   * Le categorie, già contate e ordinate come sul televisore.
   *
   * Le compone `buildGuideCategories`, la stessa funzione della guida grande: due elenchi costruiti
   * da due parti divergerebbero al primo canale senza categoria, e nessuno dei due sarebbe
   * riconoscibile come sbagliato guardandolo da solo.
   */
  readonly categories: ReadonlyArray<GuideCategory>;
  readonly activeCategoryId: string;
  readonly onPickCategory: (categoryId: string) => void;
}) {
  const translator = useTranslator();
  const [elencoAperto, setElencoAperto] = useState(false);

  /*
    Quante pastiglie entrano nella striscia lo decide `splitPhoneCategories`, nel dominio: è una
    regola — quali restano fuori, e che la scelta corrente resti sempre visibile — e lì si prova
    senza montare niente.
  */
  const { shown: mostrate, hidden: nascoste } = splitPhoneCategories(categories, activeCategoryId);
  const preferiti = new Set(favorites);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '8px 0 28px' }}>
      <h1
        style={{
          margin: 0,
          padding: '0 20px 14px',
          font: `800 28px ${FONT_STACK}`,
          letterSpacing: '-.02em',
        }}
      >
        {translator.t('menu.guide')}
      </h1>

      {/*
        Le categorie, in una striscia che scorre di lato.

        Senza, la guida è un rotolo: su un'installazione vera i canali sono dodicimila, e cercarne
        uno scorrendo non è una funzione, è una penitenza. Sul televisore la colonna delle categorie
        sta sempre a schermo; qui non ci starebbe, e una tendina nasconderebbe proprio la cosa che
        serve per prima.

        Scorre **di lato e non va a capo**: andando a capo, con quaranta categorie la striscia
        mangerebbe mezzo schermo e spingerebbe i canali sotto il bordo.
      */}
      <div
        data-phone-categories="1"
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          padding: '0 20px 14px',
          // Nasconde la barra di scorrimento dove il motore la disegna sopra il contenuto.
          scrollbarWidth: 'none',
        }}
      >
        {mostrate.map((categoria) => {
          const accesa = categoria.id === activeCategoryId;
          return (
            <button
              key={categoria.id}
              type="button"
              data-category={categoria.id}
              onClick={() => onPickCategory(categoria.id)}
              /*
                `aria-pressed` e non il solo colore: a un lettore di schermo il colore non dice
                quale filtro è attivo, ed è l'unica cosa che spiega perché l'elenco sotto è corto.
              */
              aria-pressed={accesa}
              style={{
                flex: 'none',
                // 40 punti d'altezza: sotto, il dito manca il bersaglio su una striscia stretta.
                minHeight: 40,
                padding: '0 14px',
                borderRadius: 20,
                border: `1px solid ${accesa ? colors.accent : colors.border}`,
                background: accesa ? colors.accent : 'transparent',
                color: accesa ? colors.background : colors.textMuted,
                font: `600 14px ${FONT_STACK}`,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              {categoria.translated ? translator.t(categoria.label) : categoria.label}
              {' '}
              <span style={{ opacity: 0.7 }}>{categoria.count}</span>
            </button>
          );
        })}

        {/*
          «Altro», solo quando c'è davvero dell'altro.

          Un tasto che apre un elenco vuoto è peggio di nessun tasto: promette qualcosa e non la
          mantiene, e chi lo prova una volta smette di fidarsi anche quando servirebbe.
        */}
        {nascoste.length > 0 && (
          <button
            type="button"
            data-category-more="1"
            onClick={() => setElencoAperto((prima) => !prima)}
            aria-expanded={elencoAperto}
            style={{
              flex: 'none',
              minHeight: 40,
              padding: '0 14px',
              borderRadius: 20,
              border: `1px dashed ${colors.border}`,
              background: 'transparent',
              color: colors.textMuted,
              font: `600 14px ${FONT_STACK}`,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            {translator.t('guide.category.more', { count: nascoste.length })}
          </button>
        )}
      </div>

      {/*
        Le altre, **una per riga**.

        In verticale si leggono tutte insieme invece di passare sotto il pollice: è la differenza fra
        scegliere e cercare. Si chiude scegliendo, perché restare aperto coprirebbe l'elenco dei
        canali subito dopo aver detto quale si vuole.
      */}
      {elencoAperto && nascoste.length > 0 && (
        <ul
          data-category-list="1"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: '0 20px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {nascoste.map((categoria) => (
            <li key={categoria.id}>
              <button
                type="button"
                data-category={categoria.id}
                onClick={() => {
                  onPickCategory(categoria.id);
                  setElencoAperto(false);
                }}
                style={{
                  width: '100%',
                  minHeight: 48,
                  textAlign: 'left',
                  padding: '0 14px',
                  borderRadius: 12,
                  border: `1px solid ${colors.borderFaint}`,
                  background: colors.panel,
                  color: colors.text,
                  font: `600 15px ${FONT_STACK}`,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <span>{categoria.translated ? translator.t(categoria.label) : categoria.label}</span>
                <span style={{ color: colors.textFaint, font: `500 14px ${FONT_STACK}` }}>
                  {categoria.count}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {channels.length === 0 && (
        <p style={{ padding: '30px 20px', font: `500 15px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t('phone.guide.empty')}
        </p>
      )}

      {channels.map((channel) => {
        const aperto = channel.id === openChannelId;
        /*
          Lo stesso calcolo serve alla riga chiusa e a quella aperta: la **prima** voce è quel che va
          in onda adesso, perché `openedSchedule` tiene quel che non è ancora finito e ordina per
          orario. Due funzioni diverse — una per «cosa c'è ora», una per «cosa viene dopo» — sarebbero
          due definizioni di «adesso» da tenere d'accordo.
        */
        const palinsesto = openedSchedule(programs, channel.id, nowMs, {
          reminders,
          // Chiusa serve solo la prima voce; aperta tutte. Chiedere poche righe quando è chiusa
          // evita di ordinare l'intera giornata per ogni canale a ogni ridisegno.
          limit: aperto ? undefined : 1,
        });
        const inOnda = palinsesto[0]?.tag === 'onAir' ? palinsesto[0].program : null;

        return (
          <div
            key={channel.id}
            style={{ display: 'flex', flexDirection: 'column', borderBottom: `1px solid ${colors.borderFaint}` }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px' }}>
              <button
                type="button"
                onClick={() => onToggleFavorite(channel.id)}
                aria-pressed={preferiti.has(channel.id)}
                aria-label={translator.t('phone.guide.favorite', { name: channel.name })}
                style={{
                  flex: 'none',
                  width: 44,
                  height: 44,
                  border: 0,
                  background: 'none',
                  cursor: 'pointer',
                  color: preferiti.has(channel.id) ? colors.accent : colors.textFaint,
                  font: `700 20px ${FONT_STACK}`,
                }}
              >
                {preferiti.has(channel.id) ? '★' : '☆'}
              </button>

              <button
                type="button"
                onClick={() => onToggleOpen(channel.id)}
                aria-expanded={aperto}
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: 0,
                  background: 'none',
                  color: colors.text,
                  textAlign: 'left',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  gap: 14,
                  alignItems: 'center',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    flex: 'none',
                    width: 64,
                    height: 48,
                    borderRadius: 10,
                    background: colors.surface,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    font: `800 15px ${FONT_STACK}`,
                    color: colors.textDim,
                  }}
                >
                  {/*
                    Il logo quando il catalogo lo dichiara, altrimenti le iniziali. Non un segnaposto
                    grigio uguale per tutti: su un elenco di duecento righe il logo è il modo in cui
                    si trova un canale senza leggere.
                  */}
                  {channel.logoUrl !== null && channel.logoUrl !== '' ? (
                    <img
                      src={channel.logoUrl}
                      alt=""
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    initials(channel.name)
                  )}
                </span>

                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ font: `700 12px ${FONT_STACK}`, color: colors.textDim, flex: 'none' }}>
                      {channel.number}
                    </span>
                    <span
                      style={{
                        font: `700 15px ${FONT_STACK}`,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {channel.name}
                    </span>
                  </span>

                  {inOnda !== null ? (
                    <>
                      <span
                        style={{
                          font: `500 13px ${FONT_STACK}`,
                          color: colors.textMuted,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {inOnda.title}
                      </span>
                      {/*
                        La barra dice **quanto manca**, che è la domanda vera di chi scorre una guida:
                        un programma al minuto cinque e uno al cinquantacinque hanno lo stesso titolo
                        e non si fa la stessa scelta.
                      */}
                      <span
                        aria-hidden
                        style={{ height: 4, borderRadius: 2, background: colors.border, overflow: 'hidden' }}
                      >
                        <span
                          style={{
                            display: 'block',
                            height: '100%',
                            width: `${programProgressPercent(inOnda, nowMs)}%`,
                            background: colors.accent,
                          }}
                        />
                      </span>
                    </>
                  ) : (
                    <span style={{ font: `500 13px ${FONT_STACK}`, color: colors.textFaint }}>
                      {translator.t('phone.guide.noProgram')}
                    </span>
                  )}
                </span>
              </button>
            </div>

            {aperto && (
              <div style={{ padding: '0 20px 16px 98px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {palinsesto.length === 0 && (
                  <p style={{ margin: 0, font: `500 13px ${FONT_STACK}`, color: colors.textFaint }}>
                    {translator.t('phone.guide.noProgram')}
                  </p>
                )}

                {palinsesto.map((riga) => (
                  <ScheduleEntry
                    key={riga.program.id}
                    row={riga}
                    onPick={() =>
                      riga.tag === 'onAir'
                        ? onWatch(channel.id)
                        : onToggleReminder(channel.id, riga.program.startMs)
                    }
                  />
                ))}

                <div style={{ display: 'flex', gap: 8, paddingTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => onWatch(channel.id)}
                    style={{
                      flex: 1,
                      height: 42,
                      border: 0,
                      borderRadius: 10,
                      background: colors.text,
                      color: colors.background,
                      font: `700 14px ${FONT_STACK}`,
                      cursor: 'pointer',
                    }}
                  >
                    {translator.t('phone.guide.watchHere')}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Una voce del palinsesto aperto: orario, titolo, contrassegno.
 *
 * **Il contrassegno è il pulsante**, e tutta la riga lo è con lui: su un telefono un bersaglio di
 * poche decine di pixel in fondo a destra si manca, e mancarlo qui significa mettere un promemoria
 * al programma sbagliato.
 */
function ScheduleEntry({ row, onPick }: { readonly row: ScheduleRow; readonly onPick: () => void }) {
  const translator = useTranslator();
  const inOnda = row.tag === 'onAir';

  return (
    <button
      type="button"
      onClick={onPick}
      style={{
        border: 0,
        background: 'none',
        color: colors.text,
        textAlign: 'left',
        cursor: 'pointer',
        // 44 px anche qui: è la stessa mano che tocca.
        minHeight: 44,
        padding: '8px 0',
        display: 'flex',
        gap: 12,
        alignItems: 'center',
      }}
    >
      <span style={{ flex: 'none', width: 42, font: `600 13px ${FONT_STACK}`, color: colors.textDim }}>
        {hourMinute(row.program.startMs)}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          font: `500 14px ${FONT_STACK}`,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.program.title}
      </span>
      <span
        style={{
          flex: 'none',
          font: `700 11px ${FONT_STACK}`,
          letterSpacing: '.04em',
          color: inOnda ? colors.live : row.tag === 'reminded' ? colors.accent : colors.textDim,
        }}
      >
        {translator.t(`phone.guide.tag.${row.tag}`)}
      </span>
    </button>
  );
}

/**
 * Le iniziali di un'emittente, quando non c'è un logo.
 *
 * Due lettere al massimo: su 64 px tre iniziali diventano illeggibili, e il punto di questo
 * segnaposto è farsi riconoscere a colpo d'occhio scorrendo.
 */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((word) => word !== '')
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/** `20:45`, nell'ora locale dell'apparecchio: è quella che chi guarda ha sull'orologio. */
function hourMinute(atMs: number): string {
  return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(
    new Date(atMs),
  );
}
